import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

function CaseLotProgressStrip({ lots, activeGroupNum }) {
  const { t } = useTranslation();

  if (!lots || lots.length <= 1) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 mb-3"
      role="list"
      aria-label={t('caseWorkflowProgress.lotsOverview')}
    >
      {lots.map((lot, index) => {
        const isActive = lot.groupNum === activeGroupNum;
        const isCompleted = lot.status === 'completed';
        const pillClass = isCompleted
          ? 'bg-success/10 text-success border-success/30'
          : isActive
            ? 'bg-primary/10 text-primary border-primary/40 ring-1 ring-primary/25'
            : 'bg-muted/40 text-muted-foreground border-border';

        return (
          <React.Fragment key={lot.groupNum}>
            {index > 0 ? (
              <Icon
                name="ChevronRight"
                size={12}
                className="text-muted-foreground/35 shrink-0"
                aria-hidden
              />
            ) : null}
            <div
              role="listitem"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${pillClass}`}
              aria-current={isActive ? 'step' : undefined}
            >
              {isCompleted ? (
                <Icon name="CheckCircle" size={12} className="shrink-0" aria-hidden />
              ) : isActive ? (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 motion-reduce:animate-none animate-pulse"
                  aria-hidden
                />
              ) : null}
              <span>{t('caseWorkflowProgress.lotLabel', { number: lot.groupNum })}</span>
              <span className="tabular-nums">{lot.percent}%</span>
              {isActive && !isCompleted ? (
                <span className="text-[10px] uppercase tracking-wide font-semibold opacity-90">
                  {t('caseWorkflowProgress.lotActive')}
                </span>
              ) : null}
              {isCompleted ? (
                <span className="text-[10px] uppercase tracking-wide font-semibold opacity-80">
                  {t('caseWorkflowProgress.lotCompletedShort')}
                </span>
              ) : null}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const CaseWorkflowProgress = ({
  percent = 0,
  macroLabel = null,
  activeGroupNum = 1,
  totalLots = 1,
  lots = null,
  variant = 'compact',
  className = '',
  /** compact : masque le libellé macro (liste commandes dentiste). */
  showMacroLabel = true,
}) => {
  const { t } = useTranslation();
  const value = Math.min(100, Math.max(0, Number(percent) || 0));
  const complete = value >= 100;
  const lotNumber = activeGroupNum || 1;
  const ariaLabel = t('caseWorkflowProgress.ariaLabel', { percent: value, lot: lotNumber });

  const fillClass = complete ? 'bg-success' : 'bg-primary';
  const trackClass = 'bg-muted rounded-full overflow-hidden';

  const title =
    totalLots > 1
      ? t('caseWorkflowProgress.headerTitleLot', { lot: lotNumber })
      : t('caseWorkflowProgress.headerTitleSingle', { lot: lotNumber });

  if (variant === 'header') {
    const showPreviousLotsHint =
      totalLots > 1 && lots?.some((l) => l.status === 'completed' && l.groupNum < lotNumber);

    return (
      <div className={`w-full ${className}`}>
        <CaseLotProgressStrip lots={lots} activeGroupNum={lotNumber} />
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="min-w-0">
            <span className="text-sm font-medium text-foreground">{title}</span>
            {totalLots > 1 ? (
              <span className="ml-2 inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('caseWorkflowProgress.lotCounter', { current: lotNumber, total: totalLots })}
              </span>
            ) : null}
          </div>
          <span className="text-sm tabular-nums text-muted-foreground shrink-0">
            {value}%
            {macroLabel ? (
              <span className="text-foreground font-medium"> · {macroLabel}</span>
            ) : null}
          </span>
        </div>
        <div
          className={`h-2 w-full ${trackClass}`}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={ariaLabel}
        >
          <div
            className={`h-full ${fillClass} rounded-full transition-[width] duration-300 motion-reduce:transition-none`}
            style={{ width: `${value}%` }}
          />
        </div>
        {showPreviousLotsHint ? (
          <p className="text-xs text-muted-foreground mt-2">
            {t('caseWorkflowProgress.previousLotsCompleteHint')}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 min-w-0 max-w-[140px] ${className}`}>
      {totalLots > 1 ? (
        <span className="text-[10px] font-medium text-muted-foreground truncate">
          {t('caseWorkflowProgress.lotLabel', { number: lotNumber })}
        </span>
      ) : null}
      <span className="text-[11px] tabular-nums text-muted-foreground font-medium">{value}%</span>
      {showMacroLabel && macroLabel ? (
        <span className="text-[10px] text-muted-foreground truncate -mt-0.5" title={macroLabel}>
          {macroLabel}
        </span>
      ) : null}
      <div
        className={`h-1 w-full ${trackClass}`}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={ariaLabel}
      >
        <div
          className={`h-full ${fillClass} rounded-full transition-[width] duration-300 motion-reduce:transition-none`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

export default CaseWorkflowProgress;
